import AutoAwesome from "@mui/icons-material/AutoAwesome";
import { Box, Button, Stack, Typography } from "@mui/material";
import { alpha, keyframes } from "@mui/material/styles";
import {
  SCOUT_POSTHOG_EVENTS,
  SCOUT_UPSELL_PLACEMENTS,
} from "encamp-shared/src/instrumentation/events";

import { capturePosthog } from "../../../../instrumentation/posthog";
import { scoutHostToRuntime } from "../../../../instrumentation/scoutHost";
import { SCOUT_SUPPORT_ARTICLE } from "../ScoutInfoPanel";
import { useScoutHostContext } from "./ScoutHostContext";
import { scoutProCreditBannerCopy, type ScoutProCreditUsage } from "./scoutProCredit";

type Props = {
  usage: ScoutProCreditUsage;
};

const enter = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  from { transform: translateX(-100%); }
  to { transform: translateX(100%); }
`;

export function ScoutProCreditBanner({ usage }: Props) {
  const { host } = useScoutHostContext();
  const copy = scoutProCreditBannerCopy(usage.proCreditsRemaining);

  const handleLearnMoreClick = () => {
    capturePosthog(SCOUT_POSTHOG_EVENTS.PRO_UPSELL_LEARN_MORE_CLICKED, {
      capability: "pro_credit",
      host: scoutHostToRuntime(host),
      placement: SCOUT_UPSELL_PLACEMENTS.PRO_CREDIT_BANNER,
    });
  };

  return (
    <Box
      data-testid="scout-pro-credit-banner"
      sx={(theme) => ({
        mt: 1.5,
        mb: 1,
        p: 2,
        borderRadius: 3,
        border: 1,
        borderColor: alpha(theme.palette.primary.main, 0.4),
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.14)} 0%, ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
        position: "relative",
        overflow: "hidden",
        animation: `${enter} 320ms ease-out`,
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background: `linear-gradient(100deg, transparent 30%, ${alpha(theme.palette.common.white, 0.35)} 50%, transparent 70%)`,
          transform: "translateX(100%)",
          animation: `${shimmer} 1.4s ease-in-out 350ms 1 both`,
        },
        "@media (prefers-reduced-motion: reduce)": {
          animation: "none",
          "&::after": { display: "none" },
        },
      })}
    >
      <Stack spacing={0.5}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={(theme) => ({
              width: 36,
              height: 36,
              borderRadius: "50%",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.16)}`,
            })}
          >
            <AutoAwesome sx={{ fontSize: 20 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
              {copy.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {copy.body}
            </Typography>
            <Typography
              variant="caption"
              color="tealLight"
              sx={{ display: "block", mt: 0.75, fontWeight: 600 }}
            >
              {copy.remaining}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" justifyContent={{ xs: "center", sm: "flex-end" }}>
          <Button
            href={SCOUT_SUPPORT_ARTICLE}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            onClick={handleLearnMoreClick}
          >
            Learn about Scout Pro
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
